import { AVAILABILITY, LIST_VISIBILITY, LUX_PLUS_SCHEDULE, ONLINE_PURCHASE } from '~/consts/schedule';
import TENANTS from '~/consts/tenant';

import type { BrandSchedule, CampaignScheduleBrandRow, CampaignScheduleDateEntry } from './types';

export function getBrandDisplayName(brand: string): string {
  return Object.values(TENANTS).find((tenant) => tenant.brand === brand)?.title ?? brand;
}

// Book By Dates / Available Offline are outer, supplementary windows around the main List Visibility (or
// LuxPlus+) schedule, so they're always displayed in this fixed lifecycle order rather than array order.
const TYPE_DISPLAY_ORDER: Array<BrandSchedule['type']> = [
  AVAILABILITY,
  ONLINE_PURCHASE,
  LIST_VISIBILITY,
  LUX_PLUS_SCHEDULE,
];

// One representative schedule per type present in the group — same level of simplification the row
// already had (no attempt to reconcile per-region date variance within a single brand's group).
function buildDateEntries(schedules: Array<BrandSchedule>): Array<CampaignScheduleDateEntry> {
  return TYPE_DISPLAY_ORDER.map((type) => schedules.find((schedule) => schedule.type === type))
    .filter((schedule): schedule is BrandSchedule => !!schedule)
    .map((schedule) => ({ type: schedule.type, start: schedule.start, end: schedule.end }));
}

export function buildCampaignScheduleBrandRows(brandSchedules: Array<BrandSchedule>): Array<CampaignScheduleBrandRow> {
  const groups = new Map<string, Array<BrandSchedule>>();

  brandSchedules.forEach((schedule) => {
    const isLuxPlus = schedule.type === LUX_PLUS_SCHEDULE;
    const key = `${schedule.brand}-${isLuxPlus ? LUX_PLUS_SCHEDULE : 'standard'}`;
    const existing = groups.get(key);

    if (existing) {
      existing.push(schedule);
    } else {
      groups.set(key, [schedule]);
    }
  });

  return Array.from(groups.entries()).map(([key, schedules]) => {
    const isLuxPlus = schedules[0].type === LUX_PLUS_SCHEDULE;
    const regions = Array.from(new Set(schedules.map((schedule) => schedule.region)));

    const brandName = getBrandDisplayName(schedules[0].brand);

    return {
      key,
      brand: isLuxPlus ? `${brandName} - LuxPlus` : brandName,
      isLuxPlus,
      dateEntries: buildDateEntries(schedules),
      regions,
    };
  });
}
